import { useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as d3 from "d3";

const _MS_PER_DAY = 1000 * 60 * 60 * 24;
const _now = new Date();
const _baseDayUtc = Date.UTC(_now.getUTCFullYear(), _now.getUTCMonth(), _now.getUTCDate());

export function getDateAtIndex(days) {
    return new Date(_baseDayUtc - days * _MS_PER_DAY);
}

export function getIndexForDate(dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number);
    const utc1 = Date.UTC(y, m - 1, d);
    return Math.floor((_baseDayUtc - utc1) / _MS_PER_DAY);
}

// Normalize +HHMM → +HH:MM for Safari strict ISO 8601 compliance
export function parseCrawlDate(dateStr) {
    return new Date(String(dateStr).replace(/([+-])(\d{2})(\d{2})$/, '$1$2:$3'));
}

export function formatDateOnly(date) {
    const d = new Date(date);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export function formatDate(date) {
    return d3.timeFormat('%d %b %Y')(Date.parse(date));
}

export function formatDateTime(date) {
    return d3.timeFormat('%d %b %Y · %H:%M')(new Date(date));
}

export function formatDateTimeUtc(date) {
    return d3.utcFormat('%d %b %Y · %H:%M')(new Date(date));
}

// Safe display formatter for YYYY-MM-DD strings — parses date parts directly to
// avoid UTC-to-local conversion shifting the displayed day.
export function formatDisplayDate(dateStr) {
    if (!dateStr) return dateStr;
    const [y, m, d] = dateStr.split('-').map(Number);
    return d3.timeFormat('%d %b %Y')(new Date(y, m - 1, d));
}

export function usePushParams() {
    const history = useHistory();
    const location = useLocation();
    const locationRef = useRef(location);
    locationRef.current = location;
    return useCallback((updates) => {
        const loc = locationRef.current;
        const p = new URLSearchParams(loc.search);
        Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === undefined) p.delete(k);
            else p.set(k, String(v));
        });
        history.push({ pathname: loc.pathname, search: `?${p.toString()}` });
    }, [history]);
}

export function relativeAgo(date) {
    const ms = Date.now() - new Date(date).getTime();
    const days = Math.floor(ms / 86_400_000);
    if (days <= 0) {
        const hours = Math.floor(ms / 3_600_000);
        if (hours <= 0) return 'just now';
        return `${hours}h ago`;
    }
    if (days === 1) return 'yesterday';
    return `${days}d ago`;
}

export function isSameDay(d1, d2) {
    return d1.getUTCFullYear() === d2.getUTCFullYear() &&
        d1.getUTCMonth() === d2.getUTCMonth() &&
        d1.getUTCDate() === d2.getUTCDate();
}

export function extractDestinationUri(currentValue) {
    if (redirectChainIsValid(currentValue)) {
        return currentValue.redirectChainElements[currentValue.redirectChainElements.length - 1].destinationURI;
    }
    return "";
}

function redirectChainIsValid(currentValue) {
    return currentValue && Array.isArray(currentValue.redirectChainElements) && currentValue.redirectChainElements.length > 0;
}
